import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { skipToken } from '@reduxjs/toolkit/query'

import { useGetAcceptanceCategoriesQuery } from 'app/services/api/acceptanceCategories'
import { useBaselineInfoQuery } from 'app/services/api/baselines'
import { useGetActiveSystemScopeFilterProfileQuery } from 'app/services/api/filterprofiles'
import { getBaselineTitle, getWisTitle } from 'common/utils/mapUtils'

import MapComponent from 'features/Map'
import {
  selectRiskUnit,
  selectSavedFilter
} from 'features/RiskManagement/Components/LocalFilter/localFilterSelectors'
import {
  buildRiskFilterQuery,
  buildRiskRenderer,
  RiskMapMode
} from 'features/RiskManagement/utils/mapRenderer'

interface ICompareMapWrapper {
  baselineId?: string
  baselineId2?: string
  assessmentId?: string
  continuousRouteId?: string
  layer1Name?: string
  layer2Name?: string
}

const MapWrapper = ({
  baselineId,
  baselineId2,
  assessmentId,
  continuousRouteId,
  layer1Name,
  layer2Name
}: ICompareMapWrapper) => {
  const localFilter = useSelector(selectSavedFilter)
  const riskUnit = useSelector(selectRiskUnit)

  const { systemScopeFilterProfile } =
    useGetActiveSystemScopeFilterProfileQuery()

  const { data: acceptanceCategories } = useGetAcceptanceCategoriesQuery()

  const { currentData: baseline } = useBaselineInfoQuery(
    baselineId ?? skipToken
  )

  const { currentData: baseline2 } = useBaselineInfoQuery(
    baselineId2 ?? skipToken
  )

  const continuousRoutes = systemScopeFilterProfile?.continuousRoutes

  const renderer = useMemo(
    () =>
      buildRiskRenderer({
        acceptanceCategories,
        riskUnit
      }),
    [acceptanceCategories, riskUnit]
  )

  const filterQuery = useMemo(
    () =>
      buildRiskFilterQuery({
        continuousRoutes,
        localFilter,
        acceptanceCategories,
        riskUnit,
        continuousRouteId,
        mode: RiskMapMode.Assessment
      }),
    [
      continuousRoutes,
      continuousRouteId,
      localFilter,
      acceptanceCategories,
      riskUnit
    ]
  )

  const contentFields = [
    {
      fieldName: 'RouteName',
      label: 'Route Name'
    },
    {
      fieldName: 'FromMeasure',
      label: 'From Measure',
      format: {
        digitSeparator: true,
        places: 3
      }
    },
    {
      fieldName: 'ToMeasure',
      label: 'To Measure',
      format: {
        digitSeparator: true,
        places: 3
      }
    },
    {
      fieldName: 'FromDate',
      label: 'From Date'
    },
    {
      fieldName: 'ToDate',
      label: 'To Date'
    },
    {
      fieldName: 'RiskTotal',
      label: 'Total Risk',
      format: {
        digitSeparator: true,
        places: 2
      }
    },
    {
      fieldName: 'RiskDensity',
      label: 'Risk Density',
      format: {
        digitSeparator: true,
        places: 2
      }
    }
  ]

  const getFeatureLayer2Title = (): string => {
    if (baseline2?.baselineServiceUrl) {
      return getBaselineTitle(layer2Name)
    } else if (baseline?.wisServiceUrl) {
      return getWisTitle(layer2Name)
    } else {
      return 'Layer 2'
    }
  }

  const featureLayerTitle = getBaselineTitle(layer1Name)
  const featureLayerTitle2 = getFeatureLayer2Title()

  return (
    <div className='h-full'>
      <MapComponent
        featureLayerUrl={baseline?.baselineServiceUrl}
        featureLayerUrl2={baseline2?.baselineServiceUrl}
        wisServiceUrl={baseline?.wisServiceUrl}
        featureLayerTitle={featureLayerTitle}
        featureLayerTitle2={featureLayerTitle2}
        featureLayerFilterQuery={filterQuery}
        renderer={renderer}
        assessmentId={assessmentId}
        layer2Name={layer2Name}
        contentFields={contentFields}
        showStructureLayer
      />
    </div>
  )
}

export default MapWrapper
